/**
 * NotesPanel — reusable internal (contractor-only) notes panel.
 *
 * Lists internal notes for an owner record (estimate / invoice / contact / job),
 * with an add-note Field + Button and per-note delete. Designed to be embedded on
 * detail screens (e.g. estimate/invoice/job detail) — pass `ownerType` + `ownerId`.
 *
 * Notes are contractor-only (the backend mounts these behind requireAuth + withTenant
 * and never serializes them to any customer-facing surface). Bearer token via the
 * shared api client. NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listNotes, addNote, deleteNote, type NoteOwnerType, type NoteRow } from '../api/notes';
import { Text, Card, Button, Field } from './ui';
import { theme } from '../theme';
import { extractErrorMessage, formatDateTime } from '../lib/format';

type NotesPanelProps = {
  ownerType: NoteOwnerType;
  ownerId: string;
};

export function NotesPanel({ ownerType, ownerId }: NotesPanelProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [mutationError, setMutationError] = useState<string | null>(null);

  const queryKey = ['internal-notes', ownerType, ownerId];

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: () => listNotes(ownerType, ownerId),
    enabled: !!ownerId,
    staleTime: 15_000,
  });

  const addMutation = useMutation({
    mutationFn: (body: string) => addNote(ownerType, ownerId, body),
    onSuccess: () => {
      setDraft('');
      setMutationError(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => setMutationError(extractErrorMessage(err, 'Failed to add note.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId: string) => deleteNote(noteId),
    onSuccess: () => {
      setMutationError(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => setMutationError(extractErrorMessage(err, 'Failed to delete note.')),
  });

  function handleAdd() {
    const body = draft.trim();
    if (!body) return;
    setMutationError(null);
    addMutation.mutate(body);
  }

  const notes = data ?? [];

  return (
    <View style={styles.section}>
      <Text variant="label" color="secondary">
        Internal Notes
      </Text>

      {/* Add-note composer */}
      <Card>
        <Field
          placeholder="Add an internal note…"
          value={draft}
          onChangeText={setDraft}
          multiline
          autoCapitalize="sentences"
          accessibilityLabel="New internal note"
        />
        <Button
          title="Add note"
          size="sm"
          fullWidth={false}
          loading={addMutation.isPending}
          disabled={addMutation.isPending || !draft.trim()}
          onPress={handleAdd}
          accessibilityLabel="Add internal note"
        />
        {mutationError ? (
          <Text variant="small" color="danger" accessibilityRole="alert">
            {mutationError}
          </Text>
        ) : null}
      </Card>

      {/* Notes list / states */}
      {isLoading ? (
        <View style={styles.centeredState}>
          <ActivityIndicator
            color={theme.color.action.primary}
            accessibilityLabel="Loading notes"
          />
        </View>
      ) : isError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(error, 'Failed to load notes.')}
          </Text>
          <Button
            title="Retry"
            variant="secondary"
            size="sm"
            fullWidth={false}
            onPress={() => refetch()}
            accessibilityLabel="Retry loading notes"
          />
        </Card>
      ) : notes.length === 0 ? (
        <Text variant="body" color="secondary">
          No internal notes yet.
        </Text>
      ) : (
        <Card style={styles.notesCard}>
          {notes.map((note: NoteRow) => (
            <View key={note.id} style={styles.noteRow}>
              <View style={styles.noteBody}>
                <Text variant="body">{note.body}</Text>
                <Text variant="small" color="secondary">
                  {formatDateTime(note.created_at)}
                </Text>
              </View>
              <Button
                title="Delete"
                variant="secondary"
                size="sm"
                fullWidth={false}
                loading={deleteMutation.isPending && deleteMutation.variables === note.id}
                disabled={deleteMutation.isPending}
                onPress={() => deleteMutation.mutate(note.id)}
                accessibilityLabel="Delete note"
              />
            </View>
          ))}
        </Card>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    gap: theme.spacing.sm,
  },
  centeredState: {
    alignItems: 'center',
    paddingVertical: theme.spacing.lg,
  },
  notesCard: {
    gap: 0,
    padding: 0,
    overflow: 'hidden',
  },
  noteRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
  },
  noteBody: {
    flex: 1,
    gap: theme.spacing.xs,
  },
});
